/**
 * Version history picker — the toolbar's "switch which version" control.
 *
 * Before this, each row read "date · N sections" and nothing else, so a dozen
 * saves of the same page were indistinguishable and picking one was a guess.
 * Each row now carries the same themed miniature the appearance library uses.
 *
 * **Picking never publishes.** This component only reports which version was
 * chosen; the composer loads it into the canvas as a preview, and putting it in
 * front of buyers stays a separate, explicit act. That is the whole point of the
 * control's rework — it previously published on click.
 *
 * Presentational: no queries, no mutations.
 */
import { Check } from "lucide-react";
import { Skeleton } from "@flint/ui";
import { AppearanceThumbnail } from "./appearance-thumbnail.tsx";

export interface VersionPickerItem {
  id: string;
  createdAt: string;
  sectionCount: number;
  isPublished: boolean;
  /** Absent until the `withContent` query resolves — the thumbnail's input. */
  sections?: unknown[] | undefined;
  theme?: unknown;
}

export interface VersionPickerProps {
  items: VersionPickerItem[];
  /** Content is fetched only when the picker opens, so this is the first paint. */
  loading: boolean;
  /** The version currently loaded into the canvas, if any. */
  previewingId: string | null;
  onPick: (item: VersionPickerItem) => void;
}

/** "Aug 6, 02:39 PM" — the same format the toolbar button shows. */
export function formatVersionStamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function VersionPicker({ items, loading, previewingId, onPick }: VersionPickerProps) {
  return (
    <div className="w-[22rem]">
      <div className="border-b border-line px-3 py-2">
        <p className="text-sm font-semibold text-ink">Saved versions</p>
        <p className="mt-0.5 text-xs text-muted">
          Pick one to preview it here. Nothing reaches buyers until you make it live.
        </p>
      </div>

      {loading ? (
        <div className="flex flex-col gap-2 p-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="p-4 text-sm text-muted">No saved versions yet.</p>
      ) : (
        <ul className="max-h-[60vh] overflow-y-auto p-2">
          {items.map((v) => {
            const previewing = v.id === previewingId;
            return (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => onPick(v)}
                  aria-current={previewing ? "true" : undefined}
                  className={`flex w-full items-center gap-3 rounded-sm p-2 text-left transition hover:bg-raised ${
                    previewing ? "bg-raised ring-1 ring-accent" : ""
                  }`}
                >
                  <AppearanceThumbnail
                    sections={v.sections ?? []}
                    theme={v.theme ?? null}
                    className="h-16 w-24 shrink-0 rounded border border-line"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">
                      {formatVersionStamp(v.createdAt)}
                    </span>
                    <span className="block text-xs text-muted">
                      <span className="font-mono tabular-nums">{v.sectionCount}</span>{" "}
                      {v.sectionCount === 1 ? "section" : "sections"}
                      {previewing ? " · previewing" : ""}
                    </span>
                  </span>
                  {v.isPublished ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      <Check size={11} aria-hidden />
                      Live
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
